import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { openThreadSender, type InputDocument, type SendSnapshot, type ThreadSender } from "./send";
import { loadTranscript, type TranscriptPage, type TranscriptSelection } from "./transcript";
import { AgentPicker, type AgentReadiness } from "./AgentPicker";
import { DocumentPicker } from "./DocumentPicker";
import { UploadExpiredError } from "./upload";
import { RepositoryPicker } from "./RepositoryPicker";
import type { RepositoryContext, RunRepository } from "./repositories";

export function ThreadComposer({ selection, onPage, inline = false }: { selection: TranscriptSelection; onPage: (page: TranscriptPage) => void; inline?: boolean }) {
  const key = JSON.stringify(selection);
  const lifetime = useRef<AbortController | undefined>(undefined);
  const busyRef = useRef(false);
  const writes = useRef<Promise<SendSnapshot> | undefined>(undefined);
  const edits = useRef(0);
  const [ready, setReady] = useState<{ key: string; sender: ThreadSender; snapshot: SendSnapshot }>();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [error, setError] = useState<string>();
  const [stoppedRun, setStoppedRun] = useState<string>();
  const [revision, setRevision] = useState(0);
  const [agent, setAgent] = useState<AgentReadiness>({ ready: false });
  const [uploadExpired, setUploadExpired] = useState(false);
  const [reviewingCode, setReviewingCode] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    setReady(undefined); setError(undefined); setBusy(false); setSaveFailed(false); setStoppedRun(undefined);
    setAgent({ ready: false });
    setUploadExpired(false);
    setReviewingCode(false);
    busyRef.current = false; writes.current = undefined; edits.current++;
    void openThreadSender(selection, undefined, controller.signal).then(async (sender) => {
      const snapshot = await sender.read(controller.signal);
      if (!controller.signal.aborted) { setReady({ key, sender, snapshot }); setDraft(snapshot.draft); }
    }).catch(() => {
      if (!controller.signal.aborted) setError("Saved message state couldn't be opened. Sending is paused to avoid a duplicate.");
    });
    return () => controller.abort();
  }, [key, revision]);
  const current = ready?.key === key ? ready : undefined;
  const documents = current?.snapshot.pending?.input.artifacts ?? current?.snapshot.accepted?.message.input.artifacts ?? current?.snapshot.documents ?? [];
  const repositories = current?.snapshot.pending?.repositories ?? current?.snapshot.accepted?.message.repositories ?? current?.snapshot.repositories ?? [];

  async function changeRepositories(next: RunRepository[], context: RepositoryContext): Promise<boolean> {
    if (!current || busyRef.current) return false;
    const signal = lifetime.current!.signal;
    busyRef.current = true; setBusy(true); setError(undefined);
    try {
      await writes.current;
      const snapshot = await current.sender.saveRepositories(next, repositories, context, signal);
      if (!signal.aborted) { setReady({ ...current, snapshot }); return true; }
    } catch { if (!signal.aborted) setError("Code access couldn't be saved. Reload before sending."); }
    finally { if (!signal.aborted) { busyRef.current = false; setBusy(false); } }
    return false;
  }

  async function upload(file?: File, restartExpired = false) {
    if (!current || busyRef.current) return;
    const signal = lifetime.current!.signal;
    busyRef.current = true; setBusy(true); setError(undefined); setUploadExpired(false);
    try {
      await writes.current;
      const snapshot = file ? await current.sender.uploadFile(file, signal, documents) : await current.sender.checkUpload(restartExpired, signal);
      if (!signal.aborted) setReady({ ...current, snapshot });
    } catch (error) {
      if (!signal.aborted) {
        setUploadExpired(error instanceof UploadExpiredError);
        setError(error instanceof UploadExpiredError ? "This upload expired. Check and clear it before choosing a new file." : "The upload couldn't be confirmed. Check its saved result or resume with the original file.");
        try {
          const snapshot = await current.sender.read(signal);
          if (!signal.aborted) setReady({ ...current, snapshot });
        } catch { /* Keep the uncertain upload. Do not invent a replacement. */ }
      }
    } finally { if (!signal.aborted) { busyRef.current = false; setBusy(false); } }
  }

  async function changeDocuments(next: InputDocument[]) {
    if (!current || busyRef.current) return;
    const signal = lifetime.current!.signal;
    busyRef.current = true; setBusy(true); setError(undefined);
    try {
      await writes.current;
      const snapshot = await current.sender.saveDocuments(next, documents, signal);
      if (!signal.aborted) setReady({ ...current, snapshot });
    } catch {
      if (!signal.aborted) setError("The document selection couldn't be saved. Reload the saved message before sending.");
    } finally { if (!signal.aborted) { busyRef.current = false; setBusy(false); } }
  }

  function edit(text: string) {
    if (!current || busyRef.current) return;
    setDraft(text);
    const editID = ++edits.current;
    const signal = lifetime.current!.signal;
    const operation = current.sender.saveDraft(text, signal);
    writes.current = operation;
    void operation.then((snapshot) => {
      if (!signal.aborted && edits.current === editID) {
        setReady({ ...current, snapshot }); setSaveFailed(false); setError(undefined);
      }
    }, () => {
      if (!signal.aborted && edits.current === editID) { setSaveFailed(true); setError("This draft couldn't be saved. Save it before sending."); }
    });
  }

  async function run(action: "send" | "refresh" | "restore" | "recover") {
    if (!current || busyRef.current) return;
    if (action === "send" && ((!current.snapshot.pending && !agent.ready) || reviewingCode)) return;
    busyRef.current = true; setBusy(true); setError(undefined);
    const restoring = stoppedRun;
    setStoppedRun(undefined);
    const signal = lifetime.current!.signal;
    try {
      await writes.current;
      let snapshot = action === "send" ? await current.sender.send(draft, signal, current.snapshot.pending ? undefined : agent.id, documents, repositories, current.snapshot.pending?.id)
        : action === "restore" ? await current.sender.restoreStopped(restoring ?? "", signal)
        : action === "recover" ? await current.sender.restorePending(current.snapshot.pending?.id ?? "", signal)
        : await current.sender.refresh(signal);
      if (action === "refresh" && snapshot.accepted && ["completed", "failed", "cancelled"].includes(snapshot.accepted.state)) {
        const receipt = snapshot.accepted.message.id;
        let page = await loadTranscript(selection, undefined, signal);
        const contains = () => page.turns.some((turn) => turn.id === receipt && turn.threadId === selection.threadId);
        while (!contains() && page.nextNode) page = await loadTranscript(selection, page, signal);
        signal.throwIfAborted();
        onPage(page);
        snapshot = await current.sender.acknowledge(page.turns, signal);
        if (snapshot.accepted) {
          if (["failed", "cancelled"].includes(snapshot.accepted.state)) setStoppedRun(snapshot.accepted.message.id);
          else setError("The saved reply isn't available yet. Check again before sending another message.");
        }
      }
      if (!signal.aborted) { setReady({ ...current, snapshot }); setDraft(snapshot.draft); }
    } catch {
      if (!signal.aborted) {
        setError(action === "restore" || action === "recover" ? "The message couldn't be restored. Its saved state was kept. Check and edit it again." : "Delivery couldn't be confirmed. Reload the saved message or retry without creating a duplicate.");
        try {
          const snapshot = await current.sender.read(signal);
          const frozen = snapshot.pending ?? snapshot.accepted?.message;
          if (!signal.aborted && (!frozen || frozen.input.text === draft)) setReady({ ...current, snapshot });
        } catch { /* Keep the visible draft; never discard an uncertain send. */ }
      }
    } finally {
      if (!signal.aborted) { busyRef.current = false; setBusy(false); }
    }
  }

  const accepted = current?.snapshot.accepted;
  const pending = current?.snapshot.pending;
  const status = accepted ? ({ queued: "Message queued", running: "Working on your message", completed: "Loading reply", failed: "Work stopped", cancelled: "Work cancelled" }[accepted.state] ?? "Message accepted") : undefined;
  return <section aria-label="Send a message" className={`${inline ? "" : "sticky bottom-0 "}mt-10 border-t border-[#cfe4f1] bg-[#f5fbff]/95 py-4 backdrop-blur-xl`}>
    {accepted ? <><p className="mb-2 whitespace-pre-wrap break-words text-sm">{accepted.message.input.text}</p><p className="mb-3 text-sm text-[#52606e]" role="status">{status}</p></> : null}
    {error ? <p className="mb-3 text-sm text-[#52606e]" role="alert">{error}</p> : null}
    {!current ? <Button onClick={() => setRevision((value) => value + 1)} disabled={!error}>Reload saved message</Button> : <>
      {!accepted && !pending ? <AgentPicker sender={current.sender} disabled={busy || reviewingCode || Boolean(current.snapshot.upload)} onReady={setAgent} onSnapshot={(snapshot) => setReady((value) => value ? { ...value, snapshot } : value)} /> : null}
      {!accepted ? <><label className="sr-only" htmlFor="target-thread-message">Message</label>
      <textarea id="target-thread-message" placeholder="Message" rows={3} value={draft} disabled={busy || Boolean(pending)} onChange={(event) => edit(event.target.value)} className="w-full resize-y rounded-xl border border-[#cfe4f1] bg-white p-3 text-sm leading-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8] disabled:opacity-60" /></> : null}
      <DocumentPicker sender={current.sender} selected={documents} frozen={Boolean(accepted || pending)} disabled={busy || reviewingCode || Boolean(current.snapshot.upload)} onChange={changeDocuments} />
      <RepositoryPicker sender={current.sender} selected={repositories} frozen={Boolean(accepted || pending)} disabled={busy || Boolean(current.snapshot.upload || current.snapshot.pendingAssignment)} onChange={changeRepositories} onReviewing={setReviewingCode} />
      {!accepted && !pending ? <div className="mt-3 space-y-2 text-sm text-[#52606e]" aria-label="File upload">
        {current.snapshot.upload ? <p className="break-words">Pending upload: {current.snapshot.upload.name}</p> : null}
        <label className="block" htmlFor="thread-upload-file">{current.snapshot.upload ? "Resume with original file" : "Upload file"}</label>
        <input id="thread-upload-file" type="file" disabled={busy || reviewingCode || saveFailed || documents.length >= 32 || Boolean(current.snapshot.pendingAssignment)} onChange={(event) => {
          const file = event.target.files?.[0]; event.target.value = "";
          if (file) void upload(file);
        }} className="block w-full min-w-0 rounded-lg border border-[#cfe4f1] bg-white p-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#e0f2fe] file:px-3 file:py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8] disabled:opacity-60" />
        <p className="text-xs">Up to 100 MB, or 20 MB for images. File bytes aren't saved in this browser.</p>
        {current.snapshot.upload ? <Button variant="ghost" disabled={busy} onClick={() => void upload()}>Check upload</Button> : null}
        {uploadExpired ? <Button variant="ghost" disabled={busy} onClick={() => void upload(undefined, true)}>Check and clear expired upload</Button> : null}
        {busy ? <p role="status">Saving…</p> : null}
      </div> : null}
      <div className="mt-3 flex flex-wrap gap-3">
        {!accepted ? <Button disabled={busy || reviewingCode || saveFailed || Boolean(current.snapshot.upload || current.snapshot.recoveryAssignment) || (!pending && ((!draft.trim() && !documents.length) || !agent.ready))} onClick={() => void run("send")}>{pending ? "Retry message" : "Send"}</Button> : null}
        {accepted || pending ? <Button variant="ghost" disabled={busy} onClick={() => void run("refresh")}>Check progress</Button> : null}
        {pending ? <Button variant="ghost" disabled={busy} onClick={() => void run("recover")}>Check and edit message</Button> : null}
        {stoppedRun ? <Button variant="ghost" disabled={busy} onClick={() => void run("restore")}>Edit stopped message</Button> : null}
        {saveFailed ? <Button variant="ghost" disabled={busy} onClick={() => edit(draft)}>Save draft</Button> : null}
        {error ? <Button variant="ghost" disabled={busy} onClick={() => setRevision((value) => value + 1)}>Reload saved message</Button> : null}
      </div>
    </>}
  </section>;
}
